import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";

const PANEL_WIDTH = 420;
const PANEL_MAX_HEIGHT = 420;

const EXPLANATION =
  "Your public player name and scores may appear on leaderboards. Anonymous gameplay statistics help improve the game. No email or account is required.";

export interface OnlineFeaturesModalHandle {
  container: Phaser.GameObjects.Container;
  setVisible(visible: boolean): void;
  relayout(): void;
  destroy(): void;
}

/**
 * Shown the first time the player opens LEADERBOARD (and, unlike most
 * overlays here, reusable later from Leaderboard/Settings to change the
 * choice). Two outcomes only - `onEnable`/`onPlayOffline` - both are
 * terminal for this one showing; the caller records the choice via
 * `OnlineFeaturesStorage` and closes the modal itself.
 */
export function createOnlineFeaturesModal(
  scene: Phaser.Scene,
  depth: number,
  handlers: { onEnable: () => void; onPlayOffline: () => void; onDataPrivacy: () => void },
): OnlineFeaturesModalHandle {
  const container = scene.add.container(0, 0).setDepth(depth).setVisible(false);

  const dim = scene.add.rectangle(0, 0, 10, 10, 0x0a0c12, 0.85);
  container.add(dim);

  const panelBg = scene.add.graphics();
  container.add(panelBg);

  const title = scene.add
    .text(0, 0, "ONLINE FEATURES", {
      fontFamily: "Arial, sans-serif",
      fontSize: "22px",
      fontStyle: "bold",
      color: "#f5f7fa",
    })
    .setOrigin(0.5, 0);
  container.add(title);

  const question = scene.add
    .text(0, 0, "Enable global leaderboards and anonymous gameplay statistics?", {
      fontFamily: "Arial, sans-serif",
      fontSize: "15px",
      fontStyle: "bold",
      color: "#d7dbe6",
      align: "center",
    })
    .setOrigin(0.5, 0);
  container.add(question);

  const explanation = scene.add
    .text(0, 0, EXPLANATION, {
      fontFamily: "Arial, sans-serif",
      fontSize: "13px",
      color: "#8b93a7",
      align: "center",
    })
    .setOrigin(0.5, 0);
  container.add(explanation);

  const privacyLink = scene.add
    .text(0, 0, "DATA & PRIVACY", {
      fontFamily: "Arial, sans-serif",
      fontSize: "13px",
      fontStyle: "bold",
      color: "#5b8cff",
    })
    .setOrigin(0.5, 0)
    .setInteractive({ useHandCursor: true });
  privacyLink.on("pointerdown", () => handlers.onDataPrivacy());
  container.add(privacyLink);

  const enableButton = createButton(scene, 0, 0, "ENABLE", handlers.onEnable, { width: 200, height: 52 });
  container.add(enableButton);

  const offlineButton = createButton(scene, 0, 0, "PLAY OFFLINE", handlers.onPlayOffline, {
    width: 200,
    height: 46,
    fillColor: 0x1c2030,
    hoverColor: 0x262c40,
    textColor: "#f5f7fa",
  });
  container.add(offlineButton);

  function relayout(): void {
    const { width, height } = scene.scale;
    dim.setSize(width, height).setPosition(width / 2, height / 2);

    const panelWidth = Math.min(PANEL_WIDTH, width * 0.92);
    const panelHeight = Math.min(PANEL_MAX_HEIGHT, height * 0.9);
    const panelX = width / 2 - panelWidth / 2;
    const panelY = height / 2 - panelHeight / 2;

    panelBg.clear();
    panelBg.fillStyle(0x171a24, 1);
    panelBg.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);
    panelBg.lineStyle(1.5, GameConfig.colors.gridLine, 1);
    panelBg.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 18);

    const innerWidth = panelWidth - 48;
    question.setWordWrapWidth(innerWidth, true);
    explanation.setWordWrapWidth(innerWidth, true);

    let y = panelY + 20;
    title.setPosition(width / 2, y);
    y += 36;
    question.setPosition(width / 2, y);
    y += question.height + 12;
    explanation.setPosition(width / 2, y);
    y += explanation.height + 10;
    privacyLink.setPosition(width / 2, y);
    y += 30;

    enableButton.setPosition(width / 2, y + 26);
    offlineButton.setPosition(width / 2, y + 26 + 62);
  }

  relayout();

  return {
    container,
    setVisible(visible: boolean) {
      container.setVisible(visible);
      if (visible) relayout();
    },
    relayout,
    destroy() {
      container.destroy();
    },
  };
}
